import React, { useState } from 'react';
import { getAuth, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { validatePassword, getPasswordStrength } from '../passwordValidator';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Alert,
    LinearProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    InputAdornment,
    IconButton
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

export default function ChangePassword() {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordStrength, setPasswordStrength] = useState<any>(null);
    const [passwordValidation, setPasswordValidation] = useState<any>(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    // Visibility toggles
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const auth = getAuth();

    const handlePasswordChange = (password: string) => {
        setNewPassword(password);
        
        if (password.length > 0) {
            const strength = getPasswordStrength(password);
            setPasswordStrength(strength);
            
            const validation = validatePassword(password);
            setPasswordValidation(validation);
        } else {
            setPasswordStrength(null);
            setPasswordValidation(null);
        }
    };

    const handleSubmit = async () => {
        setError('');
        setSuccess('');

        // Validation
        if (!currentPassword || !newPassword || !confirmPassword) {
            setError('All fields are required');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('New passwords do not match');
            return;
        }

        // Validate password strength
        const validation = validatePassword(newPassword);
        if (!validation.isValid) {
            setError('Password does not meet security requirements');
            return;
        }

        setLoading(true);

        try {
            const user = auth.currentUser;

            if (!user || !user.email) {
                setError('No authenticated user found');
                setLoading(false);
                return;
            }

            // Re-authenticate user with current password
            const credential = EmailAuthProvider.credential(user.email, currentPassword);
            await reauthenticateWithCredential(user, credential);

            // Update password
            await updatePassword(user, newPassword);

            setSuccess('Password updated successfully! You will remain logged in.');
            
            // Clear form
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setPasswordStrength(null);
            setPasswordValidation(null);
        } catch (error: any) {
            console.error('Password change error:', error);
            
            if (error.code === 'auth/wrong-password') {
                setError('Current password is incorrect');
            } else if (error.code === 'auth/too-many-requests') {
                setError('Too many failed attempts. Please try again later.');
            } else {
                setError(error.message || 'Failed to update password');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <Typography variant="h5" gutterBottom>
                Change Password
            </Typography>

            <Paper sx={{ p: 3, maxWidth: 600 }}>
                <Typography variant="h6" gutterBottom>
                    Update Your Password
                </Typography>

                <Alert severity="info" sx={{ mb: 3 }}>
                    For security, you must enter your current password to set a new one.
                </Alert>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    {error && <Alert severity="error">{error}</Alert>}
                    {success && <Alert severity="success">{success}</Alert>}

                    <TextField
                        fullWidth
                        label="Current Password"
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        required
                        autoComplete="current-password"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                        edge="end"
                                    >
                                        {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    <TextField
                        fullWidth
                        label="New Password"
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => handlePasswordChange(e.target.value)}
                        required
                        autoComplete="new-password"
                        helperText="Minimum 12 characters with uppercase, lowercase, number, and special character"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                        edge="end"
                                    >
                                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    {/* Password Strength Indicator */}
                    {passwordStrength && (
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography variant="body2">
                                    Password Strength:
                                </Typography>
                                <Typography 
                                    variant="body2" 
                                    fontWeight="bold"
                                    color={passwordStrength.color}
                                >
                                    {passwordStrength.strength.toUpperCase()}
                                </Typography>
                            </Box>
                            <LinearProgress 
                                variant="determinate" 
                                value={passwordStrength.score} 
                                sx={{ 
                                    height: 8, 
                                    borderRadius: 4,
                                    '& .MuiLinearProgress-bar': {
                                        backgroundColor: passwordStrength.color
                                    }
                                }}
                            />
                        </Box>
                    )}

                    {/* Password Requirements */}
                    {passwordValidation && !passwordValidation.isValid && (
                        <Alert severity="warning">
                            <Typography variant="subtitle2" gutterBottom>
                                Password Requirements:
                            </Typography>
                            <List dense>
                                {passwordValidation.errors.map((error: string, index: number) => (
                                    <ListItem key={index} disablePadding>
                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                            <CancelIcon color="error" fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText primary={error} />
                                    </ListItem>
                                ))}
                            </List>
                        </Alert>
                    )}

                    {passwordValidation && passwordValidation.isValid && (
                        <Alert severity="success">
                            <Typography variant="subtitle2">
                                ✓ Password meets all security requirements
                            </Typography>
                        </Alert>
                    )}

                    <TextField
                        fullWidth
                        label="Confirm New Password"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        autoComplete="new-password"
                        error={confirmPassword.length > 0 && newPassword !== confirmPassword}
                        helperText={
                            confirmPassword.length > 0 && newPassword !== confirmPassword
                                ? 'Passwords do not match'
                                : ''
                        }
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        edge="end"
                                    >
                                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        disabled={loading || (passwordValidation && !passwordValidation.isValid)}
                        fullWidth
                        size="large"
                    >
                        {loading ? 'Updating Password...' : 'Update Password'}
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
}
